import { useState } from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import IconButton from "@mui/material/IconButton";

import { fetchUserToken } from "../../redux/api/auth.api";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import ContentCard from "../../components/misc/ContentCard";
import CustomButton from "../../components/CustomButton";
import TextInput from "../../components/misc/TextInput";
import Text from "../../styleguide/Text";
import SvgIcon from "../../helpers/SvgIcon";
import type { RejectedObject } from "../../helpers/apiHelper";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, "Minim 3 caractere")
    .max(50, "Maxim 50 caractere")
    .email("Adăugați un email valid")
    .required("Emailul este obligatoriu"),
  password: Yup.string()
    .min(3, "Minim 3 caractere")
    .max(50, "Maxim 50 caractere")
    .required("Adăugați o parolă"),
});

const initialValues = { email: "", password: "" };

const Login = () => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const {
    auth: { isLoadingToken },
    user: { isLoadingUser },
  } = useAppSelector((state) => ({ auth: state.auth, user: state.user }));

  const isLoading = isLoadingToken || isLoadingUser;

  const handleLogin = async (
    values: typeof initialValues,
    { setStatus }: FormikHelpers<typeof initialValues>,
  ) => {
    setStatus(undefined);
    const result = await dispatch(
      fetchUserToken({
        email_client: values.email,
        password: values.password,
      }),
    );

    if (fetchUserToken.rejected.match(result)) {
      const error = result.payload as Partial<RejectedObject> | undefined;
      setStatus(
        error?.status === 401
          ? error.message && error.message !== "Unauthorized"
            ? error.message
            : "Email sau parolă incorectă."
          : "Nu s-a putut realiza autentificarea. Încearcă din nou.",
      );
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-3 py-6">
      <ContentCard
        className="w-full max-w-[460px] !px-8 !py-10"
        cardHeaderClassName="!pt-0"
        CardHeader={() => (
          <div className="mb-8 text-center">
            <a
              href="https://www.ahead-translog.ro"
              target="_blank"
              rel="noopener noreferrer">
              <img
                src="/logo.svg"
                alt="Ahead Translog"
                width={120}
                height={85}
                className="mx-auto mb-4 block h-auto max-w-full"
              />
            </a>
            <div className="mx-auto mb-5 h-1 w-12 rounded-full bg-action" />
            <Text variant="h1" component="h1" className="!mb-2">
              Bine ai revenit
            </Text>
            <Text variant="body2" className="text-muted-dark">
              Autentificare în platforma de clienți
            </Text>
          </div>
        )}>
        <Formik
          initialValues={initialValues}
          validationSchema={loginSchema}
          onSubmit={handleLogin}>
          {({
            status,
            errors,
            touched,
            getFieldProps,
            handleSubmit,
            isSubmitting,
          }) => (
            <form className="w-full" onSubmit={handleSubmit} noValidate>
              {status && (
                <div
                  role="alert"
                  className="mb-6 rounded-control bg-danger-subtle p-4 font-normal text-danger">
                  {status}
                </div>
              )}
              <TextInput
                id="login-email"
                name="email"
                label="Email"
                type="email"
                placeholder="nume@companie.ro"
                autoFocus
                touched={touched.email}
                error={errors.email}
                inputProps={{
                  ...getFieldProps("email"),
                  autoComplete: "username",
                }}
              />
              <TextInput
                id="login-password"
                name="password"
                label="Parolă"
                type={showPassword ? "text" : "password"}
                placeholder="Introdu parola"
                touched={touched.password}
                error={errors.password}
                inputProps={{
                  ...getFieldProps("password"),
                  autoComplete: "current-password",
                }}
                endComponent={
                  <IconButton
                    type="button"
                    size="small"
                    aria-label={
                      showPassword ? "Ascunde parola" : "Arată parola"
                    }
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((visible) => !visible)}>
                    <SvgIcon
                      type={showPassword ? "VISIBILITY_OFF" : "VISIBILITY"}
                    />
                  </IconButton>
                }
              />
              <CustomButton
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                className="w-full !min-h-[48px] !text-base"
                disabled={isLoading || isSubmitting}
                loading={isLoading || isSubmitting}>
                Autentificare
              </CustomButton>
            </form>
          )}
        </Formik>
      </ContentCard>
    </main>
  );
};

export default Login;
